import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { inr } from "@/lib/format";
import { isPurchasable, OUT_OF_STOCK_LABEL } from "@/lib/inventory";
import { useStore } from "@/context/StoreContext";
import ProductImage from "@/components/ProductImage";
import { defaultProductSize } from "@/lib/sizes";
import { prefetchProductImage } from "@/lib/images";
import { prefetchProductDetail } from "@/lib/productPrefetch";
import { swatchForColor } from "@/lib/colorSwatches";

const ProductCard = ({ product, index = 0 }) => {
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const [size, setSize] = useState(() => defaultProductSize(product.sizes));
  const fav = inWishlist(product.id);
  const available = isPurchasable(product);
  const lowStock = available && Number(product.stock_count) <= 2;
  const warm = () => prefetchProductDetail(product.id, product.image);

  return (
    <div className="group relative" data-testid={`product-card-${product.id}`}>
      <Link
        to={`/product/${product.id}`}
        className="block"
        onPointerEnter={warm}
        onPointerDown={warm}
        onFocus={warm}
      >
        <div className="relative overflow-hidden bg-muted aspect-[3/4]">
          <ProductImage
            src={product.image}
            alt={product.name}
            priority={index < 4}
          />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {!available && (
              <span className="bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.22em] px-2 py-1">{OUT_OF_STOCK_LABEL}</span>
            )}
            {available && lowStock && (
              <span className="bg-background/95 text-[10px] uppercase tracking-[0.22em] px-2 py-1">Only {product.stock_count} left</span>
            )}
            {product.is_new && available && (
              <span className="bg-background/95 text-[10px] uppercase tracking-[0.22em] px-2 py-1">New</span>
            )}
            {product.compare_at_price && available && (
              <span className="bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.22em] px-2 py-1">
                Sale
              </span>
            )}
          </div>

          {/* Wishlist heart */}
          <button
            onClick={(e) => {
              e.preventDefault();
              toggleWishlist(product);
            }}
            data-testid={`wishlist-btn-${product.id}`}
            className="absolute top-3 right-3 min-h-11 min-w-11 inline-flex items-center justify-center bg-background/80 backdrop-blur-sm hover:bg-background transition"
            aria-label="Wishlist"
          >
            <Heart className={`w-4 h-4 ${fav ? "fill-primary text-primary" : "text-primary"}`} />
          </button>

          {/* Quick add */}
          <div className="quick-add">
            {available ? (
            <div className="bg-background/95 backdrop-blur-md p-2 flex items-center gap-2">
              <div className="flex-1 flex items-center gap-1 overflow-x-auto no-scrollbar">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={(e) => {
                      e.preventDefault();
                      setSize(s);
                    }}
                    data-testid={`quick-size-${product.id}-${s}`}
                    className={`text-xs px-2.5 py-2 min-h-9 border ${
                      size === s ? "border-primary bg-primary text-primary-foreground" : "border-border"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  addToCart(product, size);
                }}
                data-testid={`quick-add-${product.id}`}
                className="text-[10px] uppercase tracking-[0.22em] bg-primary text-primary-foreground px-4 py-2.5 min-h-9 hover:bg-primary/85 transition shrink-0"
              >
                Add
              </button>
            </div>
            ) : (
              <div className="bg-background/95 backdrop-blur-md p-3 text-center text-[10px] uppercase tracking-[0.22em] text-secondary">
                {OUT_OF_STOCK_LABEL}
              </div>
            )}
          </div>
        </div>
      </Link>

      <Link to={`/product/${product.id}`}>
        <div className="pt-4 pb-2">
          <p className="overline text-[10px]">{product.category_name}</p>
          <h3 className="serif text-base sm:text-lg mt-1.5 leading-tight">{product.name}</h3>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-sm font-medium" data-testid={`product-price-${product.id}`}>{inr(product.price)}</span>
            {product.compare_at_price && (
              <span className="text-xs text-secondary line-through">{inr(product.compare_at_price)}</span>
            )}
          </div>
          {product.color_options?.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`color-options-${product.id}`}>
              {product.color_options.map((option) => (
                <Link
                  key={option.id}
                  to={`/product/${option.id}`}
                  onClick={(e) => e.stopPropagation()}
                  title={option.color}
                  aria-label={option.color}
                  className={`w-4 h-4 rounded-full border ${
                    option.id === product.id ? "border-primary ring-1 ring-primary/30" : "border-border"
                  } ${!option.in_stock ? "opacity-40" : ""}`}
                  style={{ backgroundColor: swatchForColor(option.color) }}
                />
              ))}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default React.memo(ProductCard);
